import React, { useState } from 'react';
import api from '../api';
import * as XLSX from 'xlsx';
import { FaFileExcel, FaDownload, FaUpload, FaTimes, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const EmbalagemImport = ({ fecharModal, recarregar }) => {
    const [arquivo, setArquivo] = useState(null);
    const [processando, setProcessando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [erros, setErros] = useState([]);

    const gerarPlanilhaModelo = () => {
        // Criar planilha modelo
        const dados = [
            ['Nome da Embalagem', 'Custo Unitário (R$)'],
            ['Caixa 15x15 Branca', '2.50'],
            ['Saco Plástico Pequeno', '0.15'],
            ['Fita Adesiva Colorida', '3.80'],
            ['Papel Celofane', '1.20'],
            ['Caixa 20x20 Kraft', '3.50'],
        ];

        const ws = XLSX.utils.aoa_to_sheet(dados);
        
        // Definir largura das colunas
        ws['!cols'] = [
            { wch: 30 }, // Nome
            { wch: 20 }  // Custo
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Embalagens');
        
        // Salvar arquivo
        XLSX.writeFile(wb, 'modelo_embalagens.xlsx');
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setArquivo(file);
            setResultado(null);
            setErros([]);
        }
    };

    const validarLinha = (linha, index) => {
        const errosLinha = [];
        
        if (!linha.nome || linha.nome.trim() === '') {
            errosLinha.push(`Linha ${index + 2}: Nome é obrigatório`);
        }
        
        if (!linha.custo_unitario || isNaN(linha.custo_unitario) || parseFloat(linha.custo_unitario) <= 0) {
            errosLinha.push(`Linha ${index + 2}: Custo unitário deve ser um número maior que zero`);
        }
        
        return errosLinha;
    };

    const processarArquivo = async () => {
        if (!arquivo) {
            alert('Selecione um arquivo primeiro');
            return;
        }

        setProcessando(true);
        setErros([]);
        setResultado(null);

        try {
            const data = await arquivo.arrayBuffer();
            const workbook = XLSX.read(data);
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json(worksheet);

            if (json.length === 0) {
                setErros(['Planilha vazia ou formato inválido']);
                setProcessando(false);
                return;
            }

            // Validar e preparar dados
            const embalagens = [];
            const errosValidacao = [];

            json.forEach((row, index) => {
                // Mapear colunas (aceita variações de nome)
                const embalagem = {
                    nome: row['Nome da Embalagem'] || row['Nome'] || row['nome'] || '',
                    custo_unitario: row['Custo Unitário (R$)'] || row['Custo Unitário'] || row['Custo'] || row['custo_unitario'] || 0
                };

                // Limpar e converter custo
                if (typeof embalagem.custo_unitario === 'string') {
                    embalagem.custo_unitario = embalagem.custo_unitario
                        .replace('R$', '')
                        .replace(/\./g, '')
                        .replace(',', '.')
                        .trim();
                }
                embalagem.custo_unitario = parseFloat(embalagem.custo_unitario);

                // Validar
                const errosLinha = validarLinha(embalagem, index);
                if (errosLinha.length > 0) {
                    errosValidacao.push(...errosLinha);
                } else {
                    embalagens.push({
                        nome: embalagem.nome.trim(),
                        custo_unitario: embalagem.custo_unitario
                    });
                }
            });

            if (errosValidacao.length > 0) {
                setErros(errosValidacao);
                setProcessando(false);
                return;
            }

            // Enviar para API
            const response = await api.post('/api/embalagens/importar', {
                embalagens: embalagens
            });

            setResultado({
                sucesso: response.data.sucesso || 0,
                erros: response.data.erros || [],
                total: embalagens.length
            });

            if (response.data.sucesso > 0) {
                setTimeout(() => {
                    recarregar();
                }, 2000);
            }

        } catch (error) {
            console.error('Erro ao processar arquivo:', error);
            if (error.response?.data?.erros) {
                setErros(error.response.data.erros);
            } else {
                setErros(['Erro ao processar arquivo. Verifique o formato.']);
            }
        } finally {
            setProcessando(false);
        }
    };

    return (
        <div 
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4"
            style={{ zIndex: 10000 }}
        >
            <div 
                className="bg-white rounded-lg shadow-lg w-full max-w-2xl relative"
                style={{ maxHeight: '90vh', overflowY: 'auto' }}
            >
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-700 flex items-center gap-2">
                        <FaFileExcel className="text-green-600" />
                        Importar Embalagens
                    </h2>
                    <button
                        onClick={fecharModal}
                        className="text-gray-400 hover:text-gray-600 transition"
                    >
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="p-6">
                    {/* Instruções */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <h3 className="font-semibold text-blue-800 mb-2">📋 Como importar:</h3>
                        <ol className="text-sm text-blue-700 space-y-1 ml-4">
                            <li>1. Baixe a planilha modelo</li>
                            <li>2. Preencha com os dados das embalagens</li>
                            <li>3. Salve o arquivo Excel</li>
                            <li>4. Faça o upload aqui</li>
                        </ol>
                    </div>

                    {/* Botão Baixar Modelo */}
                    <button
                        onClick={gerarPlanilhaModelo}
                        className="w-full bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 transition flex items-center justify-center gap-2 mb-4"
                    >
                        <FaDownload />
                        Baixar Planilha Modelo
                    </button>

                    {/* Upload */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4 text-center">
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            onChange={handleFileChange}
                            className="hidden"
                            id="fileInput"
                        />
                        <label
                            htmlFor="fileInput"
                            className="cursor-pointer flex flex-col items-center gap-2"
                        >
                            <FaUpload className="text-4xl text-gray-400" />
                            <span className="text-gray-600">
                                {arquivo ? arquivo.name : 'Clique para selecionar arquivo Excel'}
                            </span>
                            <span className="text-xs text-gray-500">
                                Formatos aceitos: .xlsx, .xls
                            </span>
                        </label>
                    </div>

                    {/* Botão Processar */}
                    <button
                        onClick={processarArquivo}
                        disabled={!arquivo || processando}
                        className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {processando ? 'Processando...' : 'Importar Embalagens'}
                    </button>

                    {/* Resultado */}
                    {resultado && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-2 text-green-700 font-semibold mb-2">
                                <FaCheckCircle />
                                Importação Concluída
                            </div>
                            <p className="text-sm text-green-600">
                                {resultado.sucesso} de {resultado.total} embalagens importadas com sucesso
                            </p>
                            {resultado.erros && resultado.erros.length > 0 && (
                                <div className="mt-2">
                                    <p className="text-sm text-red-600 font-semibold">Erros:</p>
                                    <ul className="text-xs text-red-600 ml-4">
                                        {resultado.erros.map((erro, index) => (
                                            <li key={index}>{erro}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Erros de Validação */}
                    {erros.length > 0 && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
                                <FaTimesCircle />
                                Erros Encontrados
                            </div>
                            <ul className="text-sm text-red-600 space-y-1 max-h-40 overflow-y-auto">
                                {erros.map((erro, index) => (
                                    <li key={index}>• {erro}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmbalagemImport;